import { useState } from "react";
import { User, LogOut, UserCircle, Download, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { ExportModal } from "@/components/forms/ExportModal";
import { ImportTransactions } from "@/components/transactions/ImportTransactions";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export function UserMenu() {
  const { signOut } = useAuth();

  const { openModal } = useProfileModal();
  const [isOpen, setIsOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success("Você foi desconectado com sucesso.");
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
      toast.error("Erro ao fazer logout. Tente novamente.");
    }
  };

  const handleProfile = () => {
    setIsOpen(false);
    openModal();
  };

  const handleExport = () => {
    setIsOpen(false);
    setExportModalOpen(true);
  };

  const handleImport = () => {
    setIsOpen(false);
    setImportModalOpen(true);
  };

  return (
    <>
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="bg-primary/10 hover:bg-primary/20 rounded-full p-2 h-12 w-12 sm:h-10 sm:w-10 transition-colors border border-primary/20 hover:border-primary/30 shadow-sm hover:shadow-md touch-manipulation"
          data-testid="user-menu-button"
        >
          <User className="h-5 w-5 text-primary" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-48 bg-popover border border-border rounded-lg shadow-lg mt-2 z-50"
        sideOffset={8}
      >
        <DropdownMenuItem 
          onClick={handleProfile}
          className="flex items-center gap-2 px-3 py-3 sm:py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors touch-manipulation min-h-[44px] sm:min-h-auto"
        >
          <UserCircle className="h-4 w-4" />
          Perfil
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={handleExport}
          className="flex items-center gap-2 px-3 py-3 sm:py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors touch-manipulation min-h-[44px] sm:min-h-auto"
        >
          <Download className="h-4 w-4" />
          Exportar Dados
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={handleImport}
          className="flex items-center gap-2 px-3 py-3 sm:py-2 text-sm cursor-pointer hover:bg-accent rounded-md transition-colors touch-manipulation min-h-[44px] sm:min-h-auto"
        >
          <Upload className="h-4 w-4" />
          Importar Dados
        </DropdownMenuItem>
        <DropdownMenuSeparator className="my-1 bg-border" />
        <DropdownMenuItem 
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-3 sm:py-2 text-sm cursor-pointer hover:bg-destructive hover:text-destructive-foreground rounded-md transition-colors touch-manipulation min-h-[44px] sm:min-h-auto"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <ExportModal 
      open={exportModalOpen} 
      onOpenChange={setExportModalOpen} 
    />
    <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
          <ImportTransactions onImportSuccess={() => setImportModalOpen(false)} />
        </DialogContent>
      </Dialog>
    </>
  );
}